import React, { Component } from "react";
import ReactTable from "react-table-6";
import "react-table-6/react-table.css";
import MenuBar from "./navbar";
import Header from "./header";
import { BASE_URL, isTokenValid } from "./utility";
import { Navigate } from "react-router-dom";

class InvoiceReport extends Component {
  constructor() {
    super();
    this.state = {
      type: "",
      selectedState: "",
      items: [],
      invoiceSearch: "",
      mobilePage: 0,
      pageSize: 10,
      redirectToLogin: false,
    };
  }

  getInvoiceData = (item) => {
    if (!item || !item.data) return {};

    if (typeof item.data === "object") {
      return item.data;
    }

    try {
      return JSON.parse(item.data);
    } catch (err) {
      console.error("Invalid invoice data:", item, err);
      return {};
    }
  };

  getInvoiceDateFromText = (text) => {
    const match = String(text || "").match(
      /"(?:orderDate|date|invoiceDate|deliveryNoteDate|buyerDate)"\s*:\s*"([^"]+)"/
    );

    return match ? match[1].split(" ")[0] : "";
  };

  getInvoiceDate = (data, item) => {
    const rawDate =
      data.orderDate ||
      data.date ||
      data.invoiceDate ||
      data.deliveryNoteDate ||
      data.buyerDate ||
      "";

    return rawDate
      ? String(rawDate).split(" ")[0]
      : this.getInvoiceDateFromText(item && item.data);
  };

  getInvoicePayload = (item) =>
    typeof item.data === "string" ? item.data : JSON.stringify(item.data || {});

  // 🔐 Helper: Fetch wrapper that auto-handles 401 and JSON parsing
  safeFetch = async (url, options = {}) => {
    try {
      const res = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
          ...options.headers,
        },
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        this.setState({ redirectToLogin: true });
        return null;
      }

      return res;
    } catch (err) {
      console.error("Fetch Error:", err);
      return null;
    }
  };

  // Unified dropdown handler
  handleDropdownUpdate = (field, value) => {
    this.setState({ [field]: value }, async () => {
      const { selectedState, type } = this.state;

      if (selectedState && type) {
        const res = await this.safeFetch(
          `${BASE_URL}/getInvoiceDetails/${type}/${selectedState}`,
          { method: "GET" }
        );

        if (!res) return; // already redirected

        const responseJson = await res.json();

        const result = responseJson.map((item) => {
          const data = this.getInvoiceData(item);
          return {
            refnum: item.refnum,
            gstType: item.gstType,
            date: this.getInvoiceDate(data, item),
            data: this.getInvoicePayload(item),
          };
        });

        this.setState({ items: result, invoiceSearch: "", mobilePage: 0 });
      }
    });
  };

  // Download invoice
  handleDownload = async (invoice) => {
    if (!invoice || !invoice.data) return;

    const res = await this.safeFetch(`${BASE_URL}downloadInvoice`, {
      method: "POST",
      body: invoice.data,
    });

    if (!res) return;

    const blob = await res.blob();
    const file = window.URL.createObjectURL(blob);
    window.location.assign(file);
  };

  // Delete invoice
  handleDelete = async (invoice) => {
    if (!invoice || !invoice.refnum) return;

    const value = invoice.refnum;

    if (!window.confirm(`Are you sure you want to delete invoice: ${value}?`)) {
      return;
    }

    const url = `${BASE_URL}deleteInvoice/${this.state.type}/${this.state.selectedState}/${value}`;

    const res = await this.safeFetch(url, { method: "DELETE" });
    if (!res) return;

    alert("Invoice deleted successfully!");
    window.location.reload();
  };

  renderInvoiceActions = (invoice) => (
    <div className="invoice-report-actions">
      <button
        type="button"
        onClick={() => this.handleDownload(invoice)}
        className="btn-download"
      >
        Download
      </button>
      <button
        type="button"
        onClick={() => this.handleDelete(invoice)}
        className="btn-delete"
      >
        Delete
      </button>
    </div>
  );

  handleMobilePageChange = (nextPage) => {
    this.setState({ mobilePage: nextPage });
  };

  handlePageSizeChange = (e) => {
    this.setState({
      pageSize: Number(e.target.value),
      mobilePage: 0,
    });
  };

  handleInvoiceSearchChange = (e) => {
    this.setState({
      invoiceSearch: e.target.value,
      mobilePage: 0,
    });
  };

  render() {
    // 🔐 Redirect if token expired
    if (this.state.redirectToLogin) return <Navigate to="/auth" replace />;

    // 🔐 Redirect if token missing
    if (!isTokenValid()) return <Navigate to="/auth" replace />;

    const { items, invoiceSearch, mobilePage, pageSize } = this.state;
    const normalizedInvoiceSearch = invoiceSearch.trim().toLowerCase();
    const filteredItems = normalizedInvoiceSearch
      ? items.filter((item) =>
          String(item.refnum || "")
            .toLowerCase()
            .includes(normalizedInvoiceSearch)
        )
      : items;
    const pageCount = Math.max(Math.ceil(filteredItems.length / pageSize), 1);
    const safeMobilePage = Math.min(mobilePage, pageCount - 1);
    const mobileStartIndex = safeMobilePage * pageSize;
    const mobileItems = filteredItems.slice(
      mobileStartIndex,
      mobileStartIndex + pageSize
    );

    const columns = [
      {
        Header: "Invoice No.",
        accessor: "refnum",
        minWidth: 120,
      },
      {
        Header: "Tax Type",
        accessor: "gstType",
        minWidth: 100,
      },
      {
        Header: "Date",
        accessor: "date",
        minWidth: 130,
      },
      {
        Header: "Action",
        accessor: "data",
        minWidth: 180,
        Cell: (cellInfo) =>
          this.renderInvoiceActions(cellInfo.original || cellInfo.row || cellInfo),
      },
    ];

    return (
      <div
        className="invoice-report"
        style={{
          backgroundImage: `url(${BASE_URL + "download/general/bg"})`,
          minHeight: "100vh",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Header />
        <MenuBar />

        <center className="invoice-report-content">
          <h2 className="invoice-report-title">Invoice Reports</h2>

          <div className="invoice-report-filters">
            <select
              className="invoice-report-select"
              onChange={(e) =>
                this.handleDropdownUpdate("selectedState", e.target.value)
              }
            >
              <option value="">Select State</option>
              <option value="delhi">Delhi</option>
              <option value="odisha">Odisha</option>
            </select>

            <select
              className="invoice-report-select"
              onChange={(e) =>
                this.handleDropdownUpdate("type", e.target.value)
              }
            >
              <option value="">Select Report Type</option>
              <option value="tax">Tax Invoice</option>
              <option value="pro">Pro forma Invoice</option>
              <option value="credit">Credit Note</option>
              <option value="challan">Delivery Challans</option>
            </select>
          </div>

          {items.length > 0 && (
            <div className="invoice-report-table-wrapper">
              <div className="invoice-report-search-bar">
                <input
                  type="search"
                  className="invoice-report-search"
                  placeholder="Search by invoice no."
                  value={invoiceSearch}
                  onChange={this.handleInvoiceSearchChange}
                />
              </div>

              <div className="invoice-report-table-scroll">
                <ReactTable
                  className="-striped -highlight"
                  data={filteredItems}
                  columns={columns}
                  defaultPageSize={10}
                  noDataText="No invoice found"
                  resizable={false}
                  style={styles.table}
                />
              </div>

              <div className="invoice-report-mobile-list">
                {mobileItems.map((item) => (
                  <article className="invoice-report-card" key={item.refnum}>
                    <div className="invoice-report-card-header">
                      <span className="invoice-report-card-label">
                        Invoice No.
                      </span>
                      <strong className="invoice-report-card-value">
                        {item.refnum}
                      </strong>
                    </div>

                    <div className="invoice-report-card-row">
                      <span className="invoice-report-card-label">
                        Tax Type
                      </span>
                      <span className="invoice-report-card-value">
                        {item.gstType}
                      </span>
                    </div>

                    <div className="invoice-report-card-row">
                      <span className="invoice-report-card-label">Date</span>
                      <span className="invoice-report-card-value">
                        {item.date}
                      </span>
                    </div>

                    {this.renderInvoiceActions(item)}
                  </article>
                ))}

                <div className="invoice-report-mobile-pagination">
                  <button
                    type="button"
                    className="invoice-report-page-button"
                    disabled={safeMobilePage === 0}
                    onClick={() =>
                      this.handleMobilePageChange(safeMobilePage - 1)
                    }
                  >
                    Previous
                  </button>

                  <span className="invoice-report-page-status">
                    Page {safeMobilePage + 1} of {pageCount}
                  </span>

                  <button
                    type="button"
                    className="invoice-report-page-button"
                    disabled={safeMobilePage >= pageCount - 1}
                    onClick={() =>
                      this.handleMobilePageChange(safeMobilePage + 1)
                    }
                  >
                    Next
                  </button>
                </div>

                <div className="invoice-report-page-size-bar">
                  <label
                    className="invoice-report-page-size-label"
                    htmlFor="invoice-report-page-size"
                  >
                    Records per page
                  </label>
                  <select
                    id="invoice-report-page-size"
                    className="invoice-report-page-size-select"
                    value={pageSize}
                    onChange={this.handlePageSizeChange}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="invoice-report-count">
                <span className="invoice-report-count-label">
                  {invoiceSearch ? "Matching Records" : "Total Records"}
                </span>
                <span className="invoice-report-count-value">
                  {filteredItems.length}
                </span>
              </div>
            </div>
          )}
        </center>
      </div>
    );
  }
}

// 🎨 Styles
const styles = {
  table: {
    border: "none",
    minWidth: "640px",
    width: "100%",
  },
};

// 💅 Button Styles
const invoiceReportStyleId = "invoice-report-responsive-styles";

if (
  typeof document !== "undefined" &&
  !document.getElementById(invoiceReportStyleId)
) {
  const styleSheet = document.createElement("style");
  styleSheet.id = invoiceReportStyleId;
  styleSheet.innerHTML = `
  .invoice-report-content {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    padding: 20px clamp(12px, 4vw, 32px) 0;
    width: 100%;
  }

  .invoice-report-title {
    color: white;
    font-size: clamp(1.5rem, 5vw, 2rem);
    line-height: 1.2;
    margin: 0;
    text-align: center;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  }

  .invoice-report-filters {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin-top: 20px;
    max-width: 900px;
    width: 100%;
  }

  .invoice-report-select {
    background: white;
    border: 1px solid #ccc;
    border-radius: 6px;
    box-sizing: border-box;
    color: #111827;
    font-size: 14px;
    min-height: 40px;
    min-width: 220px;
    padding: 8px 12px;
  }

  .invoice-report-table-wrapper {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
    box-sizing: border-box;
    margin-top: 30px;
    max-width: 900px;
    overflow: hidden;
    width: min(100%, 900px);
  }

  .invoice-report-table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: 100%;
  }

  .invoice-report-search-bar {
    align-items: center;
    border-bottom: 1px solid #e5e7eb;
    box-sizing: border-box;
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    width: 100%;
  }

  .invoice-report-search {
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    box-sizing: border-box;
    color: #111827;
    font-size: 14px;
    min-height: 38px;
    padding: 8px 12px;
    width: min(100%, 280px);
  }

  .invoice-report-search:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
    outline: none;
  }

  .invoice-report-page-size-bar {
    display: none;
  }

  .invoice-report-page-size-label {
    color: #374151;
    font-size: 14px;
    font-weight: 600;
  }

  .invoice-report-page-size-select {
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    color: #111827;
    font-size: 14px;
    min-height: 36px;
    padding: 6px 10px;
  }

  .invoice-report-table-scroll .ReactTable {
    border: none;
  }

  .invoice-report-table-scroll .rt-th,
  .invoice-report-table-scroll .rt-td {
    align-items: center;
    display: flex;
    min-height: 44px;
    white-space: normal;
  }

  .invoice-report-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    width: 100%;
  }

  .invoice-report-mobile-list {
    display: none;
  }

  .invoice-report-count {
    align-items: center;
    border-top: 1px solid #e5e7eb;
    color: #374151;
    display: flex;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 15px;
    font-weight: 600;
    gap: 8px;
    justify-content: flex-end;
    letter-spacing: 0;
    padding: 12px 16px;
  }

  .invoice-report-count-label {
    line-height: 1;
  }

  .invoice-report-count-value {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    color: #111827;
    font-size: 14px;
    font-weight: 800;
    line-height: 1;
    min-width: 28px;
    padding: 6px 10px;
    text-align: center;
  }

  .invoice-report-social {
    margin-top: 30px;
  }

  .btn-download, .btn-delete {
    border: none;
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
    color: white;
    font-weight: 500;
  }
  .btn-download {
    background-color: #3b82f6;
  }
  .btn-download:hover {
    background-color: #2563eb;
  }
  .btn-delete {
    background-color: #ef4444;
  }
  .btn-delete:hover {
    background-color: #dc2626;
  }

  @media (max-width: 640px) {
    .invoice-report-content {
      padding-left: 10px;
      padding-right: 10px;
    }

    .invoice-report-filters {
      align-items: stretch;
      gap: 10px;
    }

    .invoice-report-select {
      font-size: 16px;
      min-height: 44px;
      min-width: 0;
      width: 100%;
    }

    .invoice-report-table-wrapper {
      background: transparent;
      border-radius: 0;
      box-shadow: none;
      margin-top: 16px;
      overflow: visible;
      width: 100%;
    }

    .invoice-report-search-bar {
      background: white;
      border: none;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
      margin-bottom: 8px;
      padding: 8px 10px;
    }

    .invoice-report-search {
      font-size: 16px;
      min-height: 42px;
      width: 100%;
    }

    .invoice-report-page-size-bar {
      align-items: center;
      background: white;
      border: none;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
      display: flex;
      gap: 8px;
      justify-content: space-between;
      margin-bottom: 8px;
      padding: 8px 10px;
    }

    .invoice-report-page-size-label {
      font-size: 12px;
    }

    .invoice-report-page-size-select {
      font-size: 14px;
      min-height: 34px;
      min-width: 66px;
    }

    .invoice-report-table-scroll {
      display: none;
    }

    .invoice-report-mobile-list {
      display: grid;
      gap: 8px;
      width: 100%;
    }

    .invoice-report-card {
      background: white;
      border: 1px solid rgba(17, 24, 39, 0.1);
      border-radius: 8px;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.14);
      box-sizing: border-box;
      display: grid;
      gap: 7px;
      padding: 10px 12px;
      width: 100%;
    }

    .invoice-report-card-header,
    .invoice-report-card-row {
      align-items: start;
      display: flex;
      gap: 10px;
      justify-content: space-between;
    }

    .invoice-report-card-header {
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 7px;
    }

    .invoice-report-card-label {
      color: #6b7280;
      flex: 0 0 90px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0;
      text-transform: uppercase;
    }

    .invoice-report-card-value {
      color: #111827;
      flex: 1;
      font-size: 14px;
      overflow-wrap: anywhere;
      text-align: right;
    }

    .invoice-report-card .invoice-report-actions {
      display: grid;
      gap: 7px;
      grid-template-columns: 1fr 1fr;
      margin-top: 2px;
    }

    .invoice-report-card .btn-download,
    .invoice-report-card .btn-delete {
      font-size: 13px;
      min-height: 34px;
      padding: 7px 8px;
      width: 100%;
    }

    .invoice-report-mobile-pagination {
      align-items: center;
      background: white;
      border-radius: 8px;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.14);
      display: grid;
      gap: 6px;
      grid-template-columns: 1fr auto 1fr;
      padding: 8px;
      width: 100%;
    }

    .invoice-report-page-button {
      background: #111827;
      border: none;
      border-radius: 6px;
      color: white;
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
      min-height: 34px;
      padding: 7px 8px;
    }

    .invoice-report-page-button:disabled {
      background: #d1d5db;
      color: #6b7280;
      cursor: not-allowed;
    }

    .invoice-report-page-status {
      color: #111827;
      font-size: 13px;
      font-weight: 700;
      text-align: center;
      white-space: nowrap;
    }

    .invoice-report-count {
      background: white;
      border: none;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
      justify-content: center;
      margin-top: 12px;
    }
  }
`;
  document.head.appendChild(styleSheet);
}

export default InvoiceReport;
